<template>
  <div class="container mx-auto px-4 py-8">
    <div class="max-w-4xl mx-auto">
      <div v-if="loading" class="text-sm text-gray-500">{{ $t('location.loading') }}</div>

      <div v-else-if="error" class="rounded-lg bg-red-50 p-4 text-sm text-red-700">
        {{ $t('legal.loadError') }}
      </div>

      <template v-else>
        <h1 class="text-4xl font-bold text-gray-800 mb-4">
          {{ document?.title || fallbackTitle }}
        </h1>

        <div
          v-if="document?.isFallback && document?.fallbackNotice"
          class="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="status"
        >
          {{ $t('legal.fallbackNotice', { locale: document.fallbackNotice.requestedLocaleName }) }}
        </div>

        <div v-if="document" class="bg-white rounded-lg shadow-md p-8">
          <div class="text-sm text-gray-500 mb-6 flex flex-wrap gap-x-4 gap-y-1">
            <p v-if="document.publishedAt">
              {{ $t('legal.lastUpdated') }}: {{ formatDate(document.publishedAt) }}
            </p>
            <p v-if="document.version">
              {{ $t('legal.version', { n: document.version }) }}
            </p>
          </div>

          <div
            class="legal-body prose prose-gray max-w-none text-gray-600 leading-relaxed"
            v-html="document.bodyHtml"
          />
        </div>

        <div v-else class="rounded-lg bg-gray-50 p-8 text-center text-gray-600">
          {{ $t('legal.emptyDocument') }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useLegalPublicDocument } from '../../composables/useLegalPublicDocument.js';
import { getLocaleFromRoute } from '../../router/index.js';

const props = defineProps({
  documentType: {
    type: String,
    required: true,
    validator: (v) => ['privacy_policy', 'terms_of_service'].includes(v)
  },
  /** i18n key used when API title is empty */
  fallbackTitleKey: {
    type: String,
    required: true
  }
});

const { t, locale } = useI18n();
const route = useRoute();
const { document, loading, error } = useLegalPublicDocument(computed(() => props.documentType));

const fallbackTitle = computed(() => t(props.fallbackTitleKey));

function formatDate(iso) {
  if (!iso) return '';
  try {
    const routeLocale = getLocaleFromRoute(route) || locale.value;
    return new Date(iso).toLocaleDateString(routeLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return iso;
  }
}
</script>

<style scoped>
.legal-body :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.legal-body :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.legal-body :deep(p) {
  margin-bottom: 1rem;
}

.legal-body :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.legal-body :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.legal-body :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.legal-body :deep(a:hover) {
  color: #1d4ed8;
}
</style>
